import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Link,
  Typography,
  makeStyles,
  Theme,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { companyInfo, routes, themeColors } from '../../constants';
import { fetchUserSession } from '../../actions/authActions';
import rmBackground from '../../assets/img/rm_digital_solutions.jpg';

/**
 * Legal notice ("Impressum") of the RM Digital Solutions platform.
 *
 * Required by § 5 Abs. 1 ECG, § 14 UGB and § 25 MedienG for every website and
 * information-society service operated from Austria. The page is public (no
 * login) and reachable from the header menu on every page and from the portal
 * footer. Company facts come from constants/company.tsx; all wording comes
 * from the "impressum" block of public/locales/{de,en}/translation.json
 * (other languages fall back to English).
 */

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    background: `url(${rmBackground}) center center / cover`,
    minHeight: 'calc(100vH - 96px)',
    display: 'flex',
    flexDirection: 'column',
  },
  paper: {
    background: 'rgba(48, 51, 60, 0.92)',
    margin: `${theme.spacing(4)}px auto`,
    padding: theme.spacing(4),
    color: themeColors.white,
  },
  section: {
    marginTop: theme.spacing(3),
  },
  row: {
    display: 'flex',
    flexWrap: 'wrap',
    marginBottom: theme.spacing(0.5),
  },
  label: {
    fontWeight: 'bold',
    minWidth: 260,
    paddingRight: theme.spacing(2),
  },
  link: {
    color: theme.palette.primary.main,
  },
  backLink: {
    display: 'inline-block',
    marginTop: theme.spacing(4),
    color: theme.palette.primary.main,
  },
}));

interface IRowProps {
  label: string;
  children: React.ReactNode;
}

function Row(props: IRowProps) {
  const classes = useStyles();
  return (
    <div className={classes.row}>
      <Typography variant="body1" component="span" className={classes.label}>
        {props.label}
      </Typography>
      <Typography variant="body1" component="span">
        {props.children}
      </Typography>
    </div>
  );
}

export default function Impressum() {
  const classes = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const company = companyInfo;
  const securityTxtUrl = `${window.location.origin}${company.securityTxtPath}`;

  // Restore the session state for the header menu (same pattern as ProtectedRoute).
  useEffect(() => {
    dispatch(fetchUserSession(location.pathname.substr(1)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={classes.root}>
      <Container maxWidth="md" className={classes.paper}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t('impressum.title')}
        </Typography>
        <Typography variant="body2" paragraph>
          {t('impressum.legalBasis')}
        </Typography>
        <Typography variant="body1" paragraph>
          {t('impressum.operatorIntro')}
        </Typography>

        <section className={classes.section}>
          <Typography variant="h6" component="h2" gutterBottom>
            {t('impressum.providerHeading')}
          </Typography>
          <Typography variant="body1" paragraph>
            {company.name}
            <br />
            {company.street}
            <br />
            {company.city}
            <br />
            {t('impressum.country')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('impressum.phone')}:&nbsp;
            <Link href={company.phoneHref} className={classes.link}>
              {company.phone}
            </Link>
            <br />
            {t('impressum.email')}:&nbsp;
            <Link href={`mailto:${company.email}`} className={classes.link}>
              {company.email}
            </Link>
          </Typography>
        </section>

        <section className={classes.section}>
          <Typography variant="h6" component="h2" gutterBottom>
            {t('impressum.companyDetailsHeading')}
          </Typography>
          <Row label={t('impressum.legalForm')}>
            {t('impressum.legalFormValue')}
          </Row>
          <Row label={t('impressum.seat')}>{t('impressum.seatValue')}</Row>
          <Row label={t('impressum.registerNumber')}>
            {company.registerNumber}
          </Row>
          <Row label={t('impressum.registerCourt')}>
            {company.registerCourt}
          </Row>
          <Row label={t('impressum.vatId')}>{company.vatId}</Row>
          <Row label={t('impressum.management')}>
            {company.managingDirectors}
          </Row>
          <Row label={t('impressum.chamber')}>
            {t('impressum.chamberValue', { number: company.wkoMemberNumber })}
          </Row>
          <Row label={t('impressum.applicableLaw')}>
            {t('impressum.applicableLawValue')}&nbsp;(
            <Link
              href={t('impressum.applicableLawUrl')}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.link}
            >
              {t('impressum.applicableLawUrl')}
            </Link>
            )
          </Row>
          <Row label={t('impressum.supervisoryAuthority')}>
            {t('impressum.supervisoryAuthorityValue')}
          </Row>
        </section>

        <section className={classes.section}>
          <Typography variant="h6" component="h2" gutterBottom>
            {t('impressum.businessPurposeHeading')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('impressum.businessPurpose')}
          </Typography>
          <Typography variant="h6" component="h2" gutterBottom>
            {t('impressum.editorialLineHeading')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('impressum.editorialLine')}
          </Typography>
        </section>

        <section className={classes.section}>
          <Typography variant="h6" component="h2" gutterBottom>
            {t('impressum.platformContactHeading')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('impressum.platformContact')}&nbsp;
            <Link href={`mailto:${company.email}`} className={classes.link}>
              {company.email}
            </Link>
          </Typography>
        </section>

        <section className={classes.section}>
          <Typography variant="h6" component="h2" gutterBottom>
            {t('impressum.securityHeading')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('impressum.securityText')}&nbsp;
            <Link
              href={`mailto:${company.email}?subject=Security%20vulnerability%20report`}
              className={classes.link}
            >
              {company.email}
            </Link>
            <br />
            {t('impressum.securityTxtText')}&nbsp;
            <Link
              href={company.securityTxtPath}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.link}
            >
              {securityTxtUrl}
            </Link>
          </Typography>
        </section>

        <section className={classes.section}>
          <Typography variant="h6" component="h2" gutterBottom>
            {t('impressum.privacyHeading')}
          </Typography>
          <Typography variant="body1" paragraph>
            {t('impressum.privacyText')}&nbsp;
            <Link
              href={t('footer.privacyUrl')}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.link}
            >
              {t('impressum.privacyLinkLabel')}
            </Link>
            .
          </Typography>
        </section>

        <RouterLink to={routes.portal.pathName} className={classes.backLink}>
          {t('impressum.backToPortal')}
        </RouterLink>
      </Container>
    </div>
  );
}
